import React from "react";

/**
 * ErrorBoundary — catches any uncaught render/runtime error in the React tree
 * and shows a friendly recovery screen instead of a blank white page.
 * Wraps the whole SPA in App.js.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Best-effort console log — swap for Sentry / LogRocket in future.
    console.error("TikBoost ErrorBoundary caught:", error, info?.componentStack);
  }

  handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  handleHome = () => {
    window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen grid place-items-center bg-background px-6" data-testid="error-boundary">
        <div className="max-w-lg text-center">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.2em] uppercase text-cyan-400 border border-cyan-500/40 rounded-full px-3 py-1">
            Something broke
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
            We hit an <span className="bg-gradient-to-r from-cyan-300 via-cyan-400 to-pink-400 bg-clip-text text-transparent">unexpected snag</span>.
          </h1>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Don&apos;t worry — your work is safe. Try reloading the page. If it keeps happening, please contact support.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={this.handleReload}
              className="rounded-full bg-gradient-to-r from-cyan-400 to-pink-500 text-zinc-950 font-medium px-5 py-2 hover:brightness-110"
              data-testid="error-reload"
            >
              Reload page
            </button>
            <button
              onClick={this.handleHome}
              className="rounded-full border border-border px-5 py-2 hover:bg-secondary/50"
              data-testid="error-home"
            >
              Go to home
            </button>
          </div>
        </div>
      </div>
    );
  }
}
